import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../components/Toast';
import ADD from './assets/add.svg';
import DeleteModal from '../../components/atomic/DeleteModal';
import {
  fetchAllSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  fetchTasksByOrganization,
  clearOrganizationTasks,
  rewindSubscriptionTaskLimitsDates,
} from '../../slice/subscriptionSlice';
import { grantTemporaryCredit } from '../../slice/subscriptionSlice';
import { fetchAllOrganizations } from '../../slice/organizationsSlice';

const SubscriptionModal = ({ isOpen, onClose, onConfirm, initial = {} }) => {
  const dispatch = useDispatch();
  const { organizations } = useSelector(state => state.organizations);
  const { subscriptions } = useSelector(state => state.subscriptions);
  const { organizationTasks, organizationTasksLoading } = useSelector(state => state.subscriptions);
  
  const [form, setForm] = useState({
    subscription_name: '',
    subscription_description: '',
    organization_id: '',
    task_limits: [],
    is_transferable: false,
    max_pages_per_session: 0,
  });

  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // Initialize form only once when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initial && initial.subscription_id) {
        // Editing existing subscription
        setForm({
          subscription_name: initial.subscription_name || '',
          subscription_description: initial.subscription_description || '',
          organization_id: initial.organization_id || '',
          task_limits: initial.task_limits || [],
          is_transferable: !!initial.is_transferable,
          max_pages_per_session: initial.max_pages_per_session || 0,
        });
        setSelectedOrganization(initial.organization_id || null);
        
        if (initial.organization_id) {
          dispatch(fetchTasksByOrganization(initial.organization_id));
        }
      } else {
        // Creating new subscription
        setForm({
          subscription_name: '',
          subscription_description: '',
          organization_id: '',
          task_limits: [],
          is_transferable: false,
          max_pages_per_session: 0,
        });
        setSelectedOrganization(null);
        dispatch(clearOrganizationTasks());
      }
    }
  }, [isOpen]);

  const handleOrganizationChange = useCallback((organizationId) => {
    setSelectedOrganization(organizationId);
    setForm(prev => ({ ...prev, organization_id: organizationId }));
    
    if (organizationId) {
      dispatch(fetchTasksByOrganization(organizationId));
    } else {
      dispatch(clearOrganizationTasks());
    }
  }, [dispatch]);

  const handleInputChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleTaskLimitChange = useCallback((taskId, field, value) => {
    setForm(prev => {
      const existingTask = prev.task_limits.find(t => t.task_id === taskId);
      let updatedTaskLimits;
      
      if (existingTask) {
        updatedTaskLimits = prev.task_limits.map(t => 
          t.task_id === taskId ? { ...t, [field]: value } : t
        );
      } else {
        updatedTaskLimits = [...prev.task_limits, { task_id: taskId, [field]: value }];
      }
      
      return { ...prev, task_limits: updatedTaskLimits };
    });
  }, []);

  // Helper function to check if organization already has a subscription
  const getOrganizationSubscriptionInfo = (orgId) => {
    const org = organizations.find(o => o.organization_id === orgId);
    
    // Check if organization has a subscription by looking at existing subscriptions
    const existingSubscription = subscriptions.find(s => s.organization_id === orgId);
    
    if (existingSubscription) {
      return {
        hasSubscription: true,
        subscriptionName: existingSubscription.subscription_name || 'Unknown',
        subscriptionId: existingSubscription.subscription_id
      };
    }
    return { hasSubscription: false };
  };

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!form.subscription_name.trim() || !form.organization_id) return;
    
    // Submit payload depends on mode (create vs edit)
    const isEditing = !!initial.subscription_id;
    const filteredTaskLimits = form.task_limits
      .map(t => {
        const base = { task_id: t.task_id };
        if (isEditing) {
          // Edit: only submit max fields
          return {
            ...base,
            ...(typeof t.max_runs === 'number' ? { max_runs: t.max_runs } : {}),
            ...(typeof t.max_pages === 'number' ? { max_pages: t.max_pages } : {}),
          };
        }
        // Create: allow setting available_runs and max fields
        return {
          ...base,
          ...(typeof t.available_runs === 'number' ? { available_runs: t.available_runs } : {}),
          ...(typeof t.max_runs === 'number' ? { max_runs: t.max_runs } : {}),
          ...(typeof t.max_pages === 'number' ? { max_pages: t.max_pages } : {}),
        };
      })
      .filter(t => Object.keys(t).length > 1);
    
    onConfirm({
      ...form,
      task_limits: filteredTaskLimits
    });
  };

  return (
    <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg p-6 w-[800px] max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {initial.subscription_id ? 'Abonnement bearbeiten' : 'Neues Abonnement erstellen'}
        </h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Abonnement Name *</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={form.subscription_name}
            onChange={e => handleInputChange('subscription_name', e.target.value)}
            placeholder="Abonnement Name eingeben"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung (optional)</label>
          <textarea
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={form.subscription_description}
            onChange={e => handleInputChange('subscription_description', e.target.value)}
            placeholder="Beschreibung eingeben"
            rows="3"
          />
        </div>

        <div className="mb-4">
          <label className="inline-flex items-center whitespace-nowrap">
            <input
              type="checkbox"
              className="mr-2"
              checked={!!form.is_transferable}
              onChange={(e) => handleInputChange('is_transferable', e.target.checked)}
            />
            <span className="text-sm text-gray-700">Guthaben auf den Folgemonat übertragen?</span>
          </label>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Max Seiten pro Sitzung</label>
          <input
            type="number"
            min="0"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={form.max_pages_per_session}
            onChange={e => handleInputChange('max_pages_per_session', parseInt(e.target.value) || 0)}
            placeholder="0"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Organisation *</label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={form.organization_id}
            onChange={(e) => handleOrganizationChange(e.target.value)}
          >
            <option value="">Organisation auswählen...</option>
            {organizations.map(org => {
              const subscriptionInfo = getOrganizationSubscriptionInfo(org.organization_id);
              const isDisabled = subscriptionInfo.hasSubscription && (!initial.subscription_id || initial.subscription_id !== subscriptionInfo.subscriptionId);
              
              return (
                <option 
                  key={org.organization_id} 
                  value={org.organization_id}
                  disabled={isDisabled}
                >
                  {org.organization_name}
                  {subscriptionInfo.hasSubscription && (!initial.subscription_id || initial.subscription_id !== subscriptionInfo.subscriptionId) 
                    ? ` (Aktives Abonnement: ${subscriptionInfo.subscriptionName})` 
                    : ''}
                </option>
              );
            })}
          </select>
          
          {/* Show warning message for organizations with existing subscriptions */}
          {form.organization_id && (() => {
            const subscriptionInfo = getOrganizationSubscriptionInfo(form.organization_id);
            if (subscriptionInfo.hasSubscription && (!initial.subscription_id || initial.subscription_id !== subscriptionInfo.subscriptionId)) {
              return (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Organisation bereits abonniert
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          Diese Organisation hat bereits das Abonnement "{subscriptionInfo.subscriptionName}". 
                          Sie müssen zuerst das bestehende Abonnement löschen, bevor Sie ein neues erstellen können.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {selectedOrganization && (() => {
          const subscriptionInfo = getOrganizationSubscriptionInfo(selectedOrganization);
          
          // Only show task limits if this is not a new subscription for an already subscribed organization
          if (subscriptionInfo.hasSubscription && (!initial.subscription_id || initial.subscription_id !== subscriptionInfo.subscriptionId)) {
            return null;
          }
          
          return (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aufgaben-Limits für {organizations.find(o => o.organization_id === selectedOrganization)?.organization_name}
              </label>
              
              {organizationTasksLoading ? (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-custom-yellow"></div>
                </div>
              ) : organizationTasks.length > 0 ? (
                <div className="border border-gray-200 rounded-md p-4 max-h-60 overflow-y-auto">
                  <div className={`grid ${initial.subscription_id ? 'grid-cols-7' : 'grid-cols-7'} gap-4 mb-3 text-sm font-medium text-gray-700 border-b pb-2`}>
                    <div>Aufgabe</div>
                    <div>Max Läufe</div>
                    <div>Temporäre Credits</div>
                    <div>Verfügbare Läufe</div>
                    <div>Max Seiten</div>
                    <div>Verfügbare Seiten</div>
                    <div>Temporäre Seiten</div>
                  </div>
                  {organizationTasks.map(task => (
                    <div key={task.id} className={`grid ${initial.subscription_id ? 'grid-cols-7' : 'grid-cols-7'} gap-4 py-2 border-b border-gray-100`}>
                      <div className="text-sm text-gray-900">{task.name}</div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-custom-yellow"
                          placeholder="0"
                          value={(form.task_limits.find(t => t.task_id === task.id)?.max_runs ?? '')}
                          onChange={(e) => handleTaskLimitChange(task.id, 'max_runs', parseInt(e.target.value) || 0)}
                        />
                      </div>
                      <div className="text-sm text-gray-900 flex items-center">
                        {(() => {
                          const tl = form.task_limits.find(t => t.task_id === task.id);
                          const temp = tl && typeof tl.temporary_credits_given === 'number' ? tl.temporary_credits_given : 0;
                          return temp;
                        })()}
                      </div>
                      {(
                        <div className="text-sm text-gray-900 flex items-center">
                          {(() => {
                            const tl = form.task_limits.find(t => t.task_id === task.id);
                            const availableRuns = tl && typeof tl.available_runs === 'number' ? tl.available_runs : 0;
                            return availableRuns;
                          })()}
                        </div>
                      )}
                      <div>
                        <input
                          type="number"
                          min="0"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-custom-yellow"
                          placeholder="0"
                          value={(form.task_limits.find(t => t.task_id === task.id)?.max_pages ?? '')}
                          onChange={(e) => handleTaskLimitChange(task.id, 'max_pages', parseInt(e.target.value) || 0)}
                        />
                      </div>
                      <div className="text-sm text-gray-900 flex items-center">
                        {(() => {
                          const tl = form.task_limits.find(t => t.task_id === task.id);
                          const availablePages = tl && typeof tl.available_pages === 'number' ? tl.available_pages : 0;
                          return availablePages;
                        })()}
                      </div>
                      <div className="text-sm text-gray-900 flex items-center">
                        {(() => {
                          const tl = form.task_limits.find(t => t.task_id === task.id);
                          const tempPages = tl && typeof tl.temporary_pages_given === 'number' ? tl.temporary_pages_given : 0;
                          return tempPages;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">
                  Keine Aufgaben für diese Organisation gefunden.
                </div>
              )}
            </div>
          );
        })()}
        
        <div className="flex justify-end space-x-3">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={(() => {
              if (!form.subscription_name.trim() || !form.organization_id) {
                return true;
              }
              
              // Check if selected organization already has a subscription (and this is not editing that subscription)
              const subscriptionInfo = getOrganizationSubscriptionInfo(form.organization_id);
              
              if (subscriptionInfo.hasSubscription && (!initial.subscription_id || initial.subscription_id !== subscriptionInfo.subscriptionId)) {
                
                return true;
              }
              
              return false;
            })()}
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

const TemporaryCreditModal = ({ isOpen, onClose, onConfirm, subscription }) => {
  const [taskId, setTaskId] = useState('');
  const [credits, setCredits] = useState('');
  const [pages, setPages] = useState('');
  const [addRuns, setAddRuns] = useState(true);
  const [addPages, setAddPages] = useState(false);

  // Reset fields each time the dialog is opened or subscription changes
  useEffect(() => {
    if (isOpen) {
      setTaskId('');
      setCredits('');
      setPages('');
      setAddRuns(true);
      setAddPages(false);
    }
  }, [isOpen, subscription?.subscription_id]);

  if (!isOpen || !subscription) return null;

  const handleSubmit = () => {
    const runsAmount = addRuns ? parseInt(credits, 10) : NaN;
    const pagesAmount = addPages ? parseInt(pages || '0', 10) : NaN;
    const includeRuns = addRuns && !isNaN(runsAmount) && runsAmount > 0;
    const includePages = addPages && !isNaN(pagesAmount) && pagesAmount > 0;
    if (!taskId || (!includeRuns && !includePages)) return;
    onConfirm({ 
      subscription_id: subscription.subscription_id, 
      task_id: taskId, 
      ...(includeRuns ? { credits: runsAmount } : {}),
      ...(includePages ? { pages: pagesAmount } : {}),
    });
  };

  return (
    <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg p-6 w-[420px]">
        <h2 className="text-lg font-semibold mb-4">Temporary Credit vergeben</h2>

        <div className="mb-4 text-sm text-gray-700">
          <div className="mb-1 font-medium">Abonnement</div>
          <div>{subscription.subscription_name}</div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Aufgabe</label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={taskId}
            onChange={(e) => setTaskId(e.target.value)}
          >
            <option value="">Aufgabe auswählen...</option>
            {(subscription.task_limits || []).map(tl => (
              <option key={tl.task_id} value={tl.task_id}>
                {(tl.task_name || `Task ${tl.task_id}`)} (verfügbar: {tl.available_runs ?? 0}, max: {tl.max_runs ?? 0})
              </option>
            ))}
          </select>
        </div>

        <div className="mb-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={addRuns} onChange={(e) => setAddRuns(e.target.checked)} />
            Läufe (Credits) hinzufügen
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Credits</label>
          <input
            type="number"
            min="1"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={credits}
            onChange={(e) => setCredits(e.target.value)}
            placeholder="Anzahl der Credits"
            disabled={!addRuns}
          />
        </div>

        <div className="mb-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={addPages} onChange={(e) => setAddPages(e.target.checked)} />
            Seiten hinzufügen
          </label>
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Temporäre Seiten</label>
          <input
            type="number"
            min="1"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={pages}
            onChange={(e) => setPages(e.target.value)}
            placeholder="Anzahl der Seiten (optional)"
            disabled={!addPages}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={!taskId || (!((addRuns && Number(credits) > 0) || (addPages && Number(pages) > 0)))}
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

const Subscription = () => {
  const dispatch = useDispatch();
  const { subscriptions, loading } = useSelector(state => state.subscriptions);
  const { organizations } = useSelector(state => state.organizations);
  const [modalOpen, setModalOpen] = useState(false);
  const [editSubscription, setEditSubscription] = useState(null);
  const [deleteSubscriptionId, setDeleteSubscriptionId] = useState(null);
  const [tempCreditModalOpen, setTempCreditModalOpen] = useState(false);
  const [tempCreditSubscription, setTempCreditSubscription] = useState(null);
  const { addToast, ToastContainer } = useToast();

  useEffect(() => {
    dispatch(fetchAllSubscriptions());
    dispatch(fetchAllOrganizations());
  }, [dispatch]);

  const handleAdd = () => {
    setEditSubscription(null);
    setModalOpen(true);
  };

  const handleEdit = subscription => {
    setEditSubscription(subscription);
    setModalOpen(true);
  };

  const handleDelete = subscription => {
    setDeleteSubscriptionId(subscription.subscription_id);
  };

  const handleTemporaryCredit = subscription => {
    setTempCreditSubscription(subscription);
    setTempCreditModalOpen(true);
  };

  const handleModalConfirm = async (form) => {
    try {
      if (editSubscription) {
        await dispatch(updateSubscription({ ...form, subscription_id: editSubscription.subscription_id })).unwrap();
        addToast('Abonnement erfolgreich aktualisiert', 'success');
      } else {
        await dispatch(createSubscription(form)).unwrap();
        addToast('Abonnement erfolgreich erstellt', 'success');
      }
      setModalOpen(false);
      // Re-fetch subscriptions to get fresh data
      dispatch(fetchAllSubscriptions());
    } catch (err) {
      addToast('Fehler beim Speichern', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteSubscription(deleteSubscriptionId)).unwrap();
      addToast('Abonnement gelöscht', 'success');
      setDeleteSubscriptionId(null);
      // Re-fetch subscriptions to get fresh data
      dispatch(fetchAllSubscriptions());
    } catch (err) {
      addToast('Fehler beim Löschen', 'error');
    }
  };

  const handleTempCreditConfirm = async ({ subscription_id, task_id, credits, pages }) => {
    try {
      await dispatch(grantTemporaryCredit({ subscription_id, task_id, credits, pages })).unwrap();
      addToast('Temporary Credit vergeben', 'success');
      setTempCreditModalOpen(false);
      setTempCreditSubscription(null);
      dispatch(fetchAllSubscriptions());
    } catch (err) {
      addToast('Fehler beim Vergeben der Credits', 'error');
    }
  };

  const handleRewindDates = async () => {
    const confirmed = window.confirm(
      'Achtung: Dies ist eine Testaktion. Beim Bestätigen werden alle Einträge in SubscriptionTaskLimits so angepasst, dass ihr created_at um einen Monat zurückgesetzt wird.\n\nSQL: UPDATE [dbo].[SubscriptionTaskLimits] SET [created_at] = DATEADD(MONTH, -1, [created_at])\n\nMöchten Sie fortfahren?'
    );
    if (!confirmed) return;
    try {
      await dispatch(rewindSubscriptionTaskLimitsDates()).unwrap();
      addToast('Rewind erfolgreich durchgeführt', 'success');
      dispatch(fetchAllSubscriptions());
    } catch (err) {
      addToast('Fehler beim Rewind', 'error');
    }
  };

  const getOrganizationName = (organizationId) => {
    const org = organizations.find(o => o.organization_id === organizationId);
    return org ? org.organization_name : 'Unbekannt';
  };

  const formatTaskLimits = (taskLimits) => {
    if (!taskLimits || taskLimits.length === 0) return 'Keine Limits';
    
    const limits = taskLimits.map(limit => {
      const task = limit.task_name || `Task ${limit.task_id}`;
      const runs = limit.available_runs || 0;
      const pages = limit.max_pages || 0;
      return `${task}: ${runs} Läufe, ${pages} Seiten`;
    });
    
    return limits.join('; ');
  };

  return (
    <div className="mb-16 pt-5 mt-8 mx-auto px-4 sm:px-6 rounded lg:px-8">
      <ToastContainer />
      <div className="pl-32 pr-40">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold mb-6">Abonnements verwalten</h1>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center font-bold rounded-lg px-4 py-2 bg-custom-yellow text-white hover:bg-yellow-500 transition-colors shadow-sm"
          >
            <img src={ADD} alt="add" />
            <span className="ml-2">Neues Abonnement</span>
          </button>
          <button
            onClick={handleRewindDates}
            className="ml-4 flex items-center font-bold rounded-lg px-4 py-2 bg-red-500 text-white hover:bg-red-600 transition-colors shadow-sm"
            title="Test-Button: setzt created_at um 1 Monat zurück"
          >
            Rewind Date (Test)
          </button>
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-yellow"></div>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[60vh] no-scrollbar">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organisation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aufgaben-Limits</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscriptions.map(subscription => (
                  <tr key={subscription.subscription_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {subscription.subscription_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {subscription.subscription_description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {getOrganizationName(subscription.organization_id)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                      <div className="truncate" title={formatTaskLimits(subscription.task_limits)}>
                        {formatTaskLimits(subscription.task_limits)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <button
                        onClick={() => handleEdit(subscription)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleTemporaryCredit(subscription)}
                        className="text-yellow-600 hover:text-yellow-700 mr-4"
                      >
                        Temporäre Gutschrift
                      </button>
                      <button
                        onClick={() => handleDelete(subscription)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <SubscriptionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
        initial={editSubscription || {}}
      />
      <DeleteModal
        isOpen={!!deleteSubscriptionId}
        onClose={() => setDeleteSubscriptionId(null)}
        onDelete={handleDeleteConfirm}
        title="Abonnement löschen"
        message={`Möchten Sie das Abonnement "${subscriptions.find(s => s.subscription_id === deleteSubscriptionId)?.subscription_name}" wirklich löschen?`}
      />
      <TemporaryCreditModal
        isOpen={tempCreditModalOpen}
        onClose={() => { setTempCreditModalOpen(false); setTempCreditSubscription(null); }}
        onConfirm={handleTempCreditConfirm}
        subscription={tempCreditSubscription}
      />
    </div>
  );
};

export default Subscription;